import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode, RefObject } from 'react';
import { DT, DS2 } from '../../theme/designTokens';
import { acquireScrollLock, releaseScrollLock } from './modalScrollLock';
import { acquireBackgroundInert, releaseBackgroundInert } from './modalInert';

/**
 * GameplayBackdrop — the canonical, NON-DISMISSIBLE gameplay scrim.
 *
 * A specialized sibling to DS2Modal, not a variant of it: it reuses DS2Modal's
 * canonical BEHAVIOR (tokens, inert/scroll-lock primitives, focus discipline,
 * reduced-motion, entry/exit timing) without adopting its dialog SHELL —
 * there is no header/body/action-row layout, no dismissal policy, no queue.
 * Content is supplied entirely by the consumer via `children`.
 *
 * Full-viewport contract: covers and blurs the ENTIRE application —
 * sidebar, Phaser canvas, HUD, chat, everything — at DT.zIndex.overlay (80),
 * below DS2Modal's z-index (100) so a true confirmation modal (e.g. Leave
 * Game) always stacks above and remains fully usable while this is active.
 *
 * Keyboard/focus ownership is determined by LIVE DOM presence of a DS2Modal
 * root ([data-ds2-modal-root]), checked on every keydown — never by z-index
 * or listener-registration order. Whichever layer's marker is topmost owns
 * Escape and Tab: DS2Modal whenever its root is connected (in any visible
 * phase, including its own exit animation), GameplayBackdrop otherwise.
 *
 * Phase 2 scope: this component exists and is fully self-contained but is not
 * yet consumed by RoundEndOverlay/OrdinaryRoundEndOverlay.
 *
 * Intended Phase 3 usage (NOT wired up yet — documentation only):
 *
 *   const [exiting, setExiting] = useState(false);
 *   useEffect(() => {
 *     const onNextRoundReady = () => setExiting(true);
 *     window.addEventListener('phaser:next-round-ready', onNextRoundReady);
 *     return () => window.removeEventListener('phaser:next-round-ready', onNextRoundReady);
 *   }, []);
 *
 *   <GameplayBackdrop
 *     active={!exiting}
 *     onExited={() => window.dispatchEvent(
 *       new CustomEvent('react:round-end-exited', { detail: { gameId } }),
 *     )}
 *   >
 *     {ordinaryRoundEndPanel}
 *   </GameplayBackdrop>
 *
 * GameScene's existing handshake (nextRoundHandshake.ts) already tolerates
 * this: it currently receives react:round-end-exited synchronously (Phase 1);
 * once wired, it will simply arrive ~180ms later (or immediately under
 * reduced motion), which the handshake's ack/fallback semantics already
 * handle without change.
 */

/** entering → open → exiting → closed. Rendered whenever not 'closed'. */
type Phase = 'closed' | 'entering' | 'open' | 'exiting';

export interface GameplayBackdropProps {
  /** Controls mount/exit, mirroring DS2Modal's `open`. Flipping to false
   *  begins the exit transition; the component keeps rendering (still
   *  covering/blurring, still inert-locking) until the exit completes. */
  active: boolean;
  children: ReactNode;
  /**
   * Fires exactly once, when the exit transition genuinely COMPLETES after
   * `active` becomes false (immediately under reduced motion). Never fires
   * on initial mount with active=false, and never fires from an abrupt
   * unmount — an unmount before the exit timer elapses cancels the timer via
   * the owning effect's cleanup, so no completed-exit signal is sent for a
   * visual transition that never finished.
   */
  onExited?: () => void;
  /** Focus target when the overlay becomes topmost (no DS2Modal above it).
   *  Falls back to the first focusable element inside the panel, then the
   *  panel container itself. Never steals focus while a DS2Modal is open. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Optional external ref to the panel/container element — Phase 3 hook. */
  panelRef?: RefObject<HTMLDivElement | null>;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
}

const STYLE_ID = 'gameplay-backdrop-styles';

/** The minimal token-driven scaffolding this primitive needs — the backdrop's
 *  tint/blur/fallback/timing/z-index only. Deliberately NOT the DS2Modal
 *  dialog shell (no header/body/action-row CSS). */
function buildStylesheet(): string {
  return `
.gameplay-backdrop-root { position: fixed; inset: 0; z-index: ${DT.zIndex.overlay}; }
.gameplay-backdrop-scrim {
  position: fixed; inset: 0;
  background: ${DS2.modal.overlayTint};
  -webkit-backdrop-filter: blur(${DS2.modal.blurPx}px);
  backdrop-filter: blur(${DS2.modal.blurPx}px);
  opacity: 0;
  transition: opacity ${DS2.modal.transitionMs}ms ease;
  pointer-events: none;
}
@supports not ((backdrop-filter: blur(${DS2.modal.blurPx}px)) or (-webkit-backdrop-filter: blur(${DS2.modal.blurPx}px))) {
  .gameplay-backdrop-scrim { background: ${DS2.modal.overlayTintFallback}; }
}
.gameplay-backdrop-root.gameplay-backdrop-open .gameplay-backdrop-scrim { opacity: 1; }
.gameplay-backdrop-scroll {
  position: fixed; inset: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: max(env(safe-area-inset-bottom), 0px);
  pointer-events: auto;
}
@media (prefers-reduced-motion: reduce) {
  .gameplay-backdrop-scrim { transition: none; }
}
`;
}

function ensureStylesheet(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = buildStylesheet();
  document.head.appendChild(style);
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** Same focusable-element query DS2Modal's own trap uses. */
const FOCUSABLE = 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

export function GameplayBackdrop({
  active, children, onExited, initialFocusRef, panelRef: externalPanelRef,
  ariaLabel, ariaLabelledBy, ariaDescribedBy,
}: GameplayBackdropProps) {
  const internalPanelRef = useRef<HTMLDivElement>(null);
  const panelRef = externalPanelRef ?? internalPanelRef;
  const [phase, setPhase] = useState<Phase>(active ? 'entering' : 'closed');

  ensureStylesheet();

  // ── Phase machine: entrance and exit transitions (mirrors DS2Modal) ───────
  useEffect(() => {
    if (active) {
      setPhase((p) => (p === 'open' || p === 'entering' ? p : 'entering'));
    } else {
      setPhase((p) => (p === 'closed' ? p : 'exiting'));
    }
  }, [active]);

  // entering → open one frame later so the opacity transition runs.
  useEffect(() => {
    if (phase !== 'entering') return;
    if (typeof requestAnimationFrame !== 'function') { setPhase('open'); return; }
    const raf = requestAnimationFrame(() => setPhase('open'));
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  // exiting → closed after the canonical transition (immediately under
  // reduced motion). Cleared by cleanup on abrupt unmount, so a transition
  // that never finishes never fires onExited.
  useEffect(() => {
    if (phase !== 'exiting') return;
    if (prefersReducedMotion()) { setPhase('closed'); return; }
    const t = setTimeout(() => setPhase('closed'), DS2.modal.transitionMs);
    return () => clearTimeout(t);
  }, [phase]);

  // onExited fires EXACTLY ONCE — only for a genuine exiting → closed
  // transition, never for the initial closed state and never twice.
  const onExitedRef = useRef(onExited);
  useEffect(() => { onExitedRef.current = onExited; });
  const prevPhaseRef = useRef<Phase>(phase);
  useEffect(() => {
    if (phase === 'closed' && prevPhaseRef.current === 'exiting') {
      onExitedRef.current?.();
    }
    prevPhaseRef.current = phase;
  }, [phase]);

  const isActive = phase !== 'closed';

  // ── Scroll lock + background inert + focus save/restore for the whole
  //    visible lifetime (entering/open/exiting) — released only when fully
  //    closed or on abrupt unmount, so a consecutive/queued activation never
  //    briefly reactivates the background. ─────────────────────────────────
  useEffect(() => {
    if (!isActive) return;
    acquireScrollLock();
    acquireBackgroundInert();
    const previouslyFocused = document.activeElement as HTMLElement | null;
    return () => {
      releaseBackgroundInert();
      releaseScrollLock();
      // Restoration must happen AFTER inert is released — an inert element
      // silently no-ops .focus(), so restoring first would fail.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus?.();
      } else {
        (document.body as HTMLElement | null)?.focus?.();
      }
    };
  }, [isActive]);

  // Initial focus: only when GameplayBackdrop is genuinely topmost — never
  // steal focus from an already-open DS2Modal (reverse mount order).
  useEffect(() => {
    if (!isActive) return;
    if (document.querySelector('[data-ds2-modal-root]')) return;
    const panel = panelRef.current;
    if (!panel) return;
    const first = initialFocusRef?.current
      ?? panel.querySelector<HTMLElement>(FOCUSABLE)
      ?? panel;
    first.focus?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);

  // ── Keyboard ownership: WINDOW capture phase, gated LIVE on DS2Modal root
  //    presence — never on registration order or z-index. ──────────────────
  useEffect(() => {
    if (!isActive) return;
    const onKeyDown = (e: KeyboardEvent) => {
      // A connected DS2Modal root — in ANY visible phase, including its own
      // exit — owns Escape and Tab entirely. Defer without touching the event.
      if (document.querySelector('[data-ds2-modal-root]')) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();
        // Non-dismissible: swallow only. No onDismiss exists to call.
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) { e.preventDefault(); panel.focus(); return; }
      const first = focusables[0];
      const last  = focusables[focusables.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;
      if (!activeEl || !panel.contains(activeEl)) {
        e.preventDefault(); first.focus(); return;
      }
      if (e.shiftKey && activeEl === first)      { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && activeEl === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isActive, panelRef]);

  if (!isActive) return null;
  if (typeof document === 'undefined') return null;

  const rootClass = 'gameplay-backdrop-root' + (phase === 'open' ? ' gameplay-backdrop-open' : '');

  const node = (
    <div className={rootClass}>
      <div className="gameplay-backdrop-scrim" aria-hidden="true" />
      <div className="gameplay-backdrop-scroll">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          tabIndex={-1}
        >
          {children}
        </div>
      </div>
    </div>
  );

  // Exempted from modalInert's background sweep (data-ds2-inert-exempt) so
  // GameplayBackdrop never inerts itself — the same self-exemption pattern
  // DS2Modal uses via data-ds2-modal-root, on a distinct marker so neither
  // primitive's stacking logic depends on the other's marker.
  return createPortal(
    <div data-ds2-inert-exempt="true">{node}</div>,
    document.body,
  );
}
